import { authQueryKey, buildFullAuth } from '@/hooks/useAuth';

import {
  clearOktaAndLogout,
  consumePendingLogoutStorageReset,
  getAuthFromNative,
  setReturnTo,
} from '@/utils/auth';
import { placeAndroidInfoOnWindow } from '@/utils/native';
import {
  mockedAuthData,
  mockedOktaUser,
} from '@/utils/testData/loadDefaultAuthUser';

import Constants from '@/constants';

import { queryClient } from '@/services/api';

import { initializeNativeUser, initializeWebUser } from './initializeUser';
import { appInitializedAtom } from './state';

import { store as jotaiStore } from '@/atoms/store';
import { userPrefsAtom } from '@/atoms/userPrefs';
import oktaAuth, { subscribeToAuthStateChange } from '@/okta/oktaConfig';

export const initializeApp = async () => {
  consumePendingLogoutStorageReset();

  const nativeAuth = getAuthFromNative();

  if (nativeAuth.isNative) {
    placeAndroidInfoOnWindow();
    await initializeNativeUser(nativeAuth);
    jotaiStore.set(appInitializedAtom, true);
    return;
  }

  if (Constants.IS_IN_JEST_TEST) {
    const fullAuth = buildFullAuth(mockedAuthData, mockedOktaUser);
    queryClient.setQueryData(authQueryKey, fullAuth);
    jotaiStore.set(userPrefsAtom, {
      country: { id: fullAuth.user?.country },
      language: fullAuth.language,
      theme: 'light',
    });
    jotaiStore.set(appInitializedAtom, true);
    return;
  }

  // Handle the OAuth callback - parse tokens from URL before starting the service
  // This must happen before oktaAuth.start() so the tokens are available
  if (oktaAuth.token.isLoginRedirect()) {
    try {
      const { tokens } = await oktaAuth.token.parseFromUrl();
      oktaAuth.tokenManager.setTokens(tokens);
    } catch (error) {
      console.error('Error parsing tokens from URL:', error);
      // Clear any partial state and redirect to login
      await clearOktaAndLogout();
      return;
    }
  }

  let started = false;
  subscribeToAuthStateChange(async () => {
    if (jotaiStore.get(appInitializedAtom)) {
      return;
    }

    try {
      if (started) {
        return;
      }
      started = true;
      await initializeWebUser();
      jotaiStore.set(appInitializedAtom, true);
    } catch (error) {
      setReturnTo();
      console.warn(error);
    }
  });

  // Start the OktaAuth service - this triggers authStateManager.updateAuthState()
  // which will fire the subscription callback above
  await oktaAuth.start();
};
